import { create } from 'zustand';
import { API_BASE, WS_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import type { ZoomOverviewData } from '@/components/report/overview-types';

// =============================================================================
// Types
// =============================================================================

export interface OverviewStep {
  name: string;
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
}

interface OverviewState {
  status: 'idle' | 'streaming' | 'ready' | 'error';
  data: Partial<ZoomOverviewData> | null;
  steps: OverviewStep[];
  progress: { completed: number; total: number };
  error: string | null;

  fetchOverview: (accountId: string) => Promise<void>;
  clear: () => void;
}

// Default steps shown during streaming
const DEFAULT_STEPS: OverviewStep[] = [
  { name: 'account', label: 'Account info', status: 'pending' },
  { name: 'users', label: 'User list', status: 'pending' },
  { name: 'settings', label: 'Security & meeting settings', status: 'pending' },
  { name: 'plans', label: 'Account plans', status: 'pending' },
  { name: 'roles', label: 'Roles', status: 'pending' },
  { name: 'groups', label: 'Groups', status: 'pending' },
  { name: 'usage', label: 'Usage metrics', status: 'pending' },
  { name: 'recordings', label: 'Cloud recordings', status: 'pending' },
  { name: 'phone', label: 'Phone settings', status: 'pending' },
  { name: 'rooms', label: 'Zoom Rooms', status: 'pending' },
  { name: 'marketplace', label: 'Marketplace apps', status: 'pending' },
  { name: 'archive', label: 'Archive stats', status: 'pending' },
  { name: 'meetingQuality', label: 'Meeting quality scores', status: 'pending' },
  { name: 'satisfaction', label: 'Client satisfaction', status: 'pending' },
  { name: 'phoneCallLogs', label: 'Phone call logs', status: 'pending' },
  { name: 'telephone', label: 'Telephone usage', status: 'pending' },
  { name: 'liveMeetings', label: 'Live meetings', status: 'pending' },
  { name: 'roomMetrics', label: 'Room quality metrics', status: 'pending' },
  { name: 'operationLogs', label: 'Operation logs', status: 'pending' },
  { name: 'enrichment', label: 'Participant details', status: 'pending' },
  { name: 'aiSummary', label: 'ZAC Insights', status: 'pending' },
];

// =============================================================================
// Store
// =============================================================================

export const useOverviewStore = create<OverviewState>()((set, get) => ({
  status: 'idle',
  data: null,
  steps: [],
  progress: { completed: 0, total: 0 },
  error: null,

  fetchOverview: async (accountId: string) => {
    set({
      status: 'streaming',
      error: null,
      data: null,
      steps: DEFAULT_STEPS.map((s) => ({ ...s })),
      progress: { completed: 0, total: DEFAULT_STEPS.length },
    });

    try {
      await streamOverview(accountId, set, get);
    } catch {
      // Stream failed — fall back to REST
      const state = get();
      if (state.status !== 'ready') {
        await fetchOverviewRest(accountId, set);
      }
    }
  },

  clear: () => {
    set({
      status: 'idle',
      data: null,
      steps: [],
      progress: { completed: 0, total: 0 },
      error: null,
    });
  },
}));

// =============================================================================
// WebSocket streaming
// =============================================================================

type SetFn = (partial: Partial<OverviewState> | ((state: OverviewState) => Partial<OverviewState>)) => void;
type GetFn = () => OverviewState;

function streamOverview(accountId: string, set: SetFn, get: GetFn): Promise<void> {
  return new Promise((resolve, reject) => {
    const token = getToken();
    if (!token) {
      reject(new Error('Not authenticated'));
      return;
    }

    const wsUrl = `${WS_BASE}/zoom/overview/stream?accountId=${encodeURIComponent(accountId)}&token=${encodeURIComponent(token)}`;
    let ws: WebSocket;

    try {
      ws = new WebSocket(wsUrl);
    } catch {
      reject(new Error('WebSocket connection failed'));
      return;
    }

    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error('WebSocket connection timeout'));
    }, 5000);

    ws.onopen = () => {
      clearTimeout(timeout);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        handleStreamMessage(msg, set, get);

        if (msg.type === 'done') {
          ws.close();
          resolve();
        } else if (msg.type === 'error') {
          ws.close();
          reject(new Error(msg.message));
        }
      } catch {
        // Ignore parse errors
      }
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('WebSocket error'));
    };

    ws.onclose = (event) => {
      clearTimeout(timeout);
      if (!event.wasClean && get().status === 'streaming') {
        reject(new Error('Connection closed'));
      }
    };
  });
}

function handleStreamMessage(msg: any, set: SetFn, get: GetFn) {
  switch (msg.type) {
    case 'step': {
      set((state) => ({
        steps: state.steps.map((s) =>
          s.name === msg.name ? { ...s, label: msg.label, status: msg.stepStatus } : s
        ),
        progress: { completed: msg.completed, total: msg.total },
      }));
      break;
    }

    case 'section': {
      // data arrives as a JSON string from the flat message interface
      const sectionData = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
      set((state) => ({
        data: { ...state.data, [msg.name]: sectionData },
      }));
      break;
    }

    case 'done': {
      const state = get();
      const finalData = {
        ...state.data,
        fetchedAt: msg.fetchedAt,
        scope: msg.scope,
      } as ZoomOverviewData;

      set({
        status: 'ready',
        data: finalData,
        steps: state.steps.map((s) => ({
          ...s,
          status: s.status === 'pending' ? 'done' : s.status,
        })),
        progress: { completed: msg.total || state.progress.total, total: msg.total || state.progress.total },
      });
      break;
    }

    case 'error': {
      set({
        status: 'error',
        error: msg.message,
      });
      break;
    }
  }
}

// =============================================================================
// REST fallback
// =============================================================================

async function fetchOverviewRest(accountId: string, set: SetFn) {
  set({ status: 'streaming', error: null });

  try {
    const token = getToken();
    const res = await fetch(
      `${API_BASE}/zoom/overview?accountId=${encodeURIComponent(accountId)}`,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      }
    );

    if (!res.ok) {
      const body = await res.text();
      throw new Error(body || `Request failed: ${res.status}`);
    }

    const data: ZoomOverviewData = await res.json();
    set({ status: 'ready', data });
  } catch (err) {
    set({
      status: 'error',
      error: err instanceof Error ? err.message : 'Failed to fetch overview',
    });
  }
}
